// ===========================================
// Owner title (roles slice 2)
// ===========================================
// What the school calls its Admins, the owner tier: "Principal", "Director", or the default
// "Admin". Staff see it wherever the app names who can do an owner-only thing ("Only the
// Principal can…") and on the chips that mark an Admin. The switch below it chooses whether
// Messages shows that title on an Admin's messages or "School office" (off by default: a school
// decides for itself). The page is owner-only, and so is the route behind both.

import { useEffect, useState } from 'react';
import { Box, Button, FormControlLabel, Paper, Skeleton, Stack, Switch, TextField, Typography } from '@mui/material';
import {
  DEFAULT_OWNER_TITLE,
  OWNER_TITLE_MAX_LENGTH,
  useOwnerTitleSetting,
  useUpdateOwnerTitle,
} from '../../../hooks/useOwnerTitle';
import { useRoleColorScheme } from '../../../hooks/useRoleColorScheme';
import { useUpdateRoleColors } from '../../../hooks/useAdminSettings';
import ColorPickerField from '../../common/ColorPickerField';
import { getContrastTextColor } from '../../../styles/brandColors';
import { ROLE_AVATAR_COLORS } from '../../../types/avatar.types';

export default function OwnerTitleCard() {
  const { data, isLoading } = useOwnerTitleSetting();
  const update = useUpdateOwnerTitle();
  const stored = data && !data.isDefault ? data.ownerTitle : '';
  const [value, setValue] = useState(stored);

  useEffect(() => {
    setValue(stored);
  }, [stored]);

  const trimmed = value.trim();
  const tooLong = trimmed.length > OWNER_TITLE_MAX_LENGTH;
  const hasAngle = /[<>]/.test(trimmed);
  const unchanged = trimmed === stored;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        What your school calls its Admins
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Staff see this title wherever the app says who can do something they cannot (for example
        &ldquo;Only your {trimmed || DEFAULT_OWNER_TITLE} can connect Xero&rdquo;), and on the chip that marks an
        Admin. Leave it empty to use &ldquo;{DEFAULT_OWNER_TITLE}&rdquo;.
      </Typography>
      {isLoading ? (
        <Skeleton variant="rectangular" height={40} sx={{ maxWidth: 360 }} />
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <TextField
            size="small"
            label="Title"
            placeholder={DEFAULT_OWNER_TITLE}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={tooLong || hasAngle}
            helperText={
              tooLong
                ? `${OWNER_TITLE_MAX_LENGTH} characters at most`
                : hasAngle
                  ? 'The title cannot contain < or >'
                  : 'For example Principal or Director'
            }
            sx={{ width: { xs: '100%', sm: 320 } }}
            inputProps={{ 'aria-label': 'Owner title' }}
          />
          <Box sx={{ pt: { sm: 0.25 } }}>
            <Button
              variant="contained"
              onClick={() => update.mutate({ ownerTitle: trimmed || null })}
              disabled={unchanged || tooLong || hasAngle || update.isPending}
            >
              Save
            </Button>
          </Box>
        </Stack>
      )}
      {!isLoading && <OwnerColorField title={data?.ownerTitle ?? DEFAULT_OWNER_TITLE} />}
      {!isLoading && (
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={data?.inMessages ?? false}
                onChange={(e) => update.mutate({ inMessages: e.target.checked })}
                disabled={update.isPending}
              />
            }
            label={`Show “${data?.ownerTitle ?? DEFAULT_OWNER_TITLE}” on your messages`}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 6 } }}>
            When this is off, Admins appear as &ldquo;School office&rdquo; to families, staff and students in
            Messages, like the office staff. Office staff always appear as &ldquo;School office&rdquo;.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

/**
 * The owner tier's colour, beside its title, as a custom role has its own (#629). It IS the "Admin"
 * entry of Settings → Colors → User Role Colors (one stored value, two places to change it); clearing
 * it puts back the default. Saved with a button: the custom colour input fires on every drag.
 */
function OwnerColorField({ title }: { title: string }) {
  const scheme = useRoleColorScheme();
  const update = useUpdateRoleColors();
  const current = scheme.ADMIN.background;
  const [picked, setPicked] = useState<string | null>(current);

  useEffect(() => {
    setPicked(current);
  }, [current]);

  const next = picked ?? ROLE_AVATAR_COLORS.ADMIN.background;
  const unchanged = next.toUpperCase() === current.toUpperCase();

  return (
    <Box sx={{ mt: 2 }} data-testid="owner-colour">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-end' }}>
        <Box sx={{ width: { xs: '100%', sm: 320 } }}>
          <ColorPickerField
            label="Colour"
            value={picked}
            onChange={setPicked}
            helperText={`The ${title} chip and avatar colour. The same setting as ${title} in Settings → Colors → User Role Colors; clear it for the default.`}
          />
        </Box>
        <Box>
          <Button
            variant="outlined"
            onClick={() => update.mutate({ ADMIN: { background: next, text: getContrastTextColor(next) } })}
            disabled={unchanged || update.isPending}
          >
            Save colour
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
